<template>
    <el-popover
        placement="left"
        width="500"
        trigger="click"
        :ref="`localPresence${company.id}`"
        v-model="visible">
        <div v-if="step === 0">
            <div class="mb-2">
                Did you know customers can purchase LP packages on their own?
                Please have the account's billing admin purchase the LP package
                for themselves. Please
                <a href="https://aloware-company.monday.com/docs/4233513235"
                   class="text-primary"
                   target="_blank">
                    read this internal article
                </a>
                to become familiar with the LP package purchase process.
            </div>
            <el-button type="success"
                       class="w-full"
                       @click="step++">
                Continue
            </el-button>
        </div>
        <div v-if="step === 1">
            <el-form ref="change_rate_form"
                     :model="local_presence_form">
                <el-form-item label="Local Presence"
                              class="mb-2">
                    <el-select v-model="local_presence_form.local_presence"
                               placeholder="Select Option"
                               class="w-full"
                               filterable>
                        <el-option v-for="(option, id) in localPresenceOptions"
                                   v-bind:key="id"
                                   :label="option.label"
                                   :value="option.prop"
                                   :disabled="!remainingLocalPresenceToPurchase.includes(option.prop)">
                        </el-option>
                    </el-select>
                </el-form-item>

                <el-button type="success"
                           class="w-full"
                           :disabled="!local_presence_form.local_presence"
                           :loading="loading"
                           @click.prevent="purchaseLocalPresence">
                    Purchase
                </el-button>
            </el-form>
        </div>
        <button
            slot="reference"
            class="btn btn-sm btn-block blue mt-2"
            :disabled="loading || canNotPurchaseLocalPresence">
            Purchase Local Presence
        </button>
    </el-popover>
</template>

<script>
    import auth from '../../auth'
    import * as LP from '../../constants/local-presence-options'
    import * as TrialStatus from '../../constants/trial-account-status'
    import {
        addons_mixin
    } from '../../mixins'

    export default {
        name: 'local-presence-purchase',

        mixins: [addons_mixin],

        props: {
            company: {
                required: true,
                type: Object
            },
        },

        data() {
            return {
                auth: auth,
                loading: false,
                trigger: 'click',
                local_presence_form: {
                    company_id: this.company.id,
                    local_presence: null
                },
                visible: false,
                step: 1,
                TrialStatus
            }
        },

        computed: {
            remainingLocalPresenceToPurchase() {
                // Return all available types without any restrictions
                return Object.values(this.typesByCountry).flat();
            },

            hasAnyLocalPresencePackage() {
                return this.localPresenceOptions.length !== this.remainingLocalPresenceToPurchase.length
            },

            canNotPurchaseLocalPresence() {
                return this.company.trial_status !== TrialStatus.TRIAL_STATUS_SUCCESS && !this.auth?.user?.profile?.is_super_admin
            }
        },

        created() {
            var _this = this

            VueEvent.listen('LOCAL_PRESENCE_PURCHASE', data => {
                const local_presence = _this.localPresenceOptions.find(lp => lp.prop === data.localPresencePurchase.type) || {}

                if (data.localPresencePurchase.status === LP.LP_STATUS_FINISHED) {
                    this.$notify.success({
                        title: 'Local Presence Purchase Complete',
                        message: `Account can now use Local Presence in ${local_presence.label}.`,
                        duration: 5000
                    })
                }

                if (data.localPresencePurchase.status === LP.LP_STATUS_FAILED) {
                    this.$notify.error({
                        title: 'Local Presence Purchase Failed',
                        message: `Failed to purchase Local Presence for this account in ${local_presence.label}.`,
                        duration: 5000
                    })
                }

                this.$emit('purchased', data.company)
            })
        },

        methods: {
            purchaseLocalPresence() {
                const type = this.localPresenceOptions.find(lp => lp.prop === this.local_presence_form.local_presence) || {}
                this.trigger = 'manual'

                this.$confirm(`Are you sure you want to purchase local presence on ${type.label} for this account? If yes, please inform the client of this purchase. Also, remember that you don't need assistance from BillingOps to proceed with this action.`, 'Purchase Local Presence', {
                    confirmButtonText: 'Yes, I\'m sure',
                    cancelButtonText: 'No, I\'m not',
                    customClass: 'width-500 fixed',
                    type: 'info'
                }).then(res => {
                    this.loading = true

                    axios.post('/api/admin/company/purchase-local-presence', this.local_presence_form)
                        .then((res) => {
                            this.loading = false

                            this.$notify.success({
                                title: 'Local Presence Purchase Initiated',
                                message: res.data.message,
                                duration: 5000
                            })

                            this.$emit('purchased', res.data.data)

                            this.resetForm()
                        }).catch(err => {
                            console.log(err)

                            this.$root.handleErrors(err.response)
                            this.loading = false

                            this.resetForm()
                        })
                }).catch(err => {
                    this.$root.handleErrors(err.response)
                    this.resetForm()
                })
            },

            resetForm() {
                this.local_presence_form = {
                    company_id: this.company.id,
                    local_presence: null
                }

                this.trigger = 'click'
            }
        },

        watch: {
            'company.id'() {
                this.resetForm()
            },

            'visible'(visible) {
                if (visible && !this.hasAnyLocalPresencePackage) {
                    this.step = 0
                    return
                }

                this.step = 1
            }
        },

        beforeDestroy() {
            // destroy listeners
            VueEvent.unlisten('LOCAL_PRESENCE_PURCHASE');
        }
    }
</script>
